// src/components/Landing.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Ticket,
  ShieldCheck,
  Zap,
  Search,
  ArrowRight,
  Smartphone,
  CreditCard,
  ChevronRight,
  LogIn,
  LayoutDashboard,
  ChevronDown,
  X,
  Menu
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { loadUser, logout } from "../store/slices/authSlice";
import axios from "../utils/axiosConfig";
import { buildApiUrl, showMissingApiUrlAlert } from "../utils/apiConfig";
import { fetchGlobalEvents } from "../store/slices/eventSlice";
import { fetchGlobalTournaments } from "../store/slices/tournamentSlice";
import { isEventUpcoming } from "../utils/date";
import EventCard from "./events/EventCard";
import TournamentCard from "./events/TournamentCard";
import PublicFooter from "./layout/PublicFooter";

const Landing = () => {
  const [slug, setSlug] = useState("");
  const [mySalons, setMySalons] = useState([]);
  const [loadingSalons, setLoadingSalons] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingName, setOnboardingName] = useState("");
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user, loading: authLoading } = useSelector((s) => s.auth);
  const { events, loading: eventsLoading } = useSelector((s) => s.events);
  const { tournaments, loading: tournamentsLoading } = useSelector((s) => s.tournaments);

  useEffect(() => {
    dispatch(loadUser());
    dispatch(fetchGlobalEvents());
    dispatch(fetchGlobalTournaments());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMySalons();
    }
  }, [isAuthenticated]);

  const fetchMySalons = async () => {
    try {
      setLoadingSalons(true);
      const { data } = await axios.get("/api/auth/my-salons");
      setMySalons(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching salons:", err);
      setMySalons([]);
    } finally {
      setLoadingSalons(false);
    }
  };

  const handleGo = (e) => {
    e.preventDefault();
    const element = document.getElementById("events");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Efecto para scroll automático al empezar a buscar
  useEffect(() => {
    if (slug && slug.length === 1) {
      const element = document.getElementById("events");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [slug]);

  const handleLoginClick = () => {
    if (!isAuthenticated) {
      const loginUrl = buildApiUrl('/api/auth/google');
      if (!loginUrl) {
        showMissingApiUrlAlert();
        return;
      }
      window.location.href = loginUrl;
      return;
    }

    if (mySalons.length === 0) {
      setShowOnboardingModal(true);
      return;
    }

    if (mySalons.length > 0) {
      setShowPicker(true);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      setMySalons([]);
    } catch (err) {
      console.error("Error logging out:", err);
    }
  };

  const filteredEvents = React.useMemo(() => {
    // Solo mostrar eventos que no han finalizado
    const upcoming = events.filter(ev => isEventUpcoming(ev.date));

    if (!slug) return upcoming;
    const s = slug.toLowerCase();
    return upcoming.filter(ev =>
      ev.title?.toLowerCase().includes(s) ||
      ev.clientId?.nombre?.toLowerCase().includes(s) ||
      ev.location?.toLowerCase().includes(s)
    );
  }, [events, slug]);

  const filteredTournaments = React.useMemo(() => {
    const upcoming = tournaments.filter(t => t.status !== 'Finalizado' && isEventUpcoming(t.endDate || t.startDate));

    if (!slug) return upcoming;
    const s = slug.toLowerCase();
    return upcoming.filter(t =>
      t.name?.toLowerCase().includes(s) ||
      t.sport?.toLowerCase().includes(s) ||
      t.clientId?.nombre?.toLowerCase().includes(s) ||
      t.location?.toLowerCase().includes(s)
    );
  }, [tournaments, slug]);

  const handleCreateEventClick = () => {
    if (!isAuthenticated) {
      handleLoginClick();
    } else {
      setShowOnboardingModal(true);
    }
  };

  const handleOnboardingSubmit = async (e) => {
    e.preventDefault();
    if (!onboardingName.trim()) return;
    setOnboardingLoading(true);
    try {
      const generatedSlug = onboardingName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)+/g, '');
          
      const res = await axios.post('/api/clients/auto-onboarding', {
        nombre: onboardingName,
        slug: generatedSlug,
      });
      
      // Actualizar el estado de Redux antes de navegar
      await dispatch(loadUser());
      
      // Navegar directo al dashboard del nuevo perfil creador
      navigate(`/${res.data.slug}?onboarding=1`);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Hubo un error al configurar tu perfil. Intentá con otro nombre.');
    } finally {
      setOnboardingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30">
      {/* BACKGROUND DECORATION */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary-orange/10 rounded-full blur-[120px] animate-glow" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-primary-red/10 rounded-full blur-[120px] animate-glow" style={{ animationDelay: '2s' }} />
      </div>

      {/* NAVBAR */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#080808]/60 backdrop-blur-xl border-b border-white/5">
        <nav className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-10 h-10 bg-brand-gradient rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/20 group-hover:scale-110 transition-transform">
              <Ticket className="text-white" size={20} />
            </div>
            <span className="font-black tracking-tighter text-2xl">PASSAR</span>
          </Link>

          <div className="flex items-center gap-3 md:gap-6">
            <div className="hidden sm:flex items-center gap-6">
              {user?.email === 'leonardodavidcba@gmail.com' && (
                <Link to="/superadmin/dashboard" className="text-sm font-bold text-purple-400 hover:text-white transition-colors uppercase tracking-widest text-[10px] border border-purple-400/20 px-3 py-1.5 rounded-full bg-purple-400/5">Panel SuperAdmin</Link>
              )}
              <Link to="/explorar" className="text-sm font-bold text-primary-orange hover:text-white transition-colors uppercase tracking-widest text-[10px]">Explorar Socios</Link>
              <button onClick={handleCreateEventClick} className="text-sm font-bold text-white/40 hover:text-white transition-colors uppercase tracking-widest text-[10px]">Crear Evento</button>
              <Link to="/" className="text-sm font-bold text-blue-400 border border-blue-400/30 bg-blue-400/10 px-3 py-1.5 rounded-full hover:bg-blue-400 hover:text-[#0a0a0a] transition-all shadow-[0_0_15px_rgba(96,165,250,0.15)] uppercase tracking-widest text-[10px] flex items-center gap-1">Registrar Salón / Productora</Link>
              <a href="/suport" className="text-sm font-bold text-white/40 hover:text-white transition-colors uppercase tracking-widest text-[10px]">Soporte</a>
            </div>

            <div className="flex items-center gap-2">
              {isAuthenticated && (
                <button
                  onClick={handleLogout}
                  className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-full text-xs font-bold text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-all"
                >
                  <LogIn size={14} className="rotate-180" />
                  <span>Salir</span>
                </button>
              )}

              <button
                onClick={handleLoginClick}
                disabled={authLoading || loadingSalons}
                className="px-4 md:px-6 py-2 md:py-2.5 bg-white/5 border border-white/10 rounded-full text-[10px] md:text-sm font-bold hover:bg-white/10 text-white transition-all flex items-center gap-2"
              >
                {authLoading || loadingSalons ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : isAuthenticated ? (
                  <>
                    <LayoutDashboard size={16} className="text-primary-orange" />
                    <span>{mySalons.length > 0 ? "Mis Espacios" : "Ingresar"}</span>
                    {mySalons.length > 0 && <ChevronDown size={14} className="opacity-40" />}
                  </>
                ) : (
                  <>
                    <LogIn size={16} className="text-primary-orange" />
                    <span>Ingresar</span>
                  </>
                )}
              </button>

              <button
                className="sm:hidden p-2 -mr-2 text-white/40 hover:text-white transition-colors"
                onClick={() => setIsMobileMenuOpen(true)}
              >
                <Menu size={24} />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* CONTENIDO PRINCIPAL - MÁS ARRIBA Y MINIMALISTA */}
      <main className="mx-auto max-w-7xl px-6 pt-32 pb-20 relative z-10">
        <div className="flex flex-col gap-12">
          {/* BUSCADOR ESTILO COMPETENCIA (TOP LEFT) */}
          <div className="w-full max-w-xs">
            <div className="relative group">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-primary-orange transition-colors" />
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="Buscar..."
                className="w-full bg-[#121212] border border-white/10 rounded-xl py-3 pl-12 pr-4 text-sm text-white font-medium outline-none focus:border-primary-orange/50 transition-all shadow-sm"
              />
              {slug && (
                <button
                  onClick={() => setSlug("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 hover:bg-white/5 rounded-full text-white/40 hover:text-white transition-all"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {eventsLoading && tournamentsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-white/5 rounded-[40px] animate-pulse" />
              ))}
            </div>
          ) : filteredEvents.length > 0 || filteredTournaments.length > 0 ? (
            <div className="flex flex-col gap-16">
              {filteredEvents.length > 0 && (
                <div>
                  <h2 className="text-3xl font-black text-white mb-8 tracking-tighter">Próximos Eventos</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                    {filteredEvents.map((ev) => (
                      <div key={ev._id} className="animate-fade-in-up" style={{ animationDelay: `${(filteredEvents.indexOf(ev) % 6) * 0.1}s` }}>
                        <EventCard ev={ev} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {filteredTournaments.length > 0 && (
                <div>
                  <h2 className="text-3xl font-black text-white mb-8 tracking-tighter">Torneos Destacados</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                    {filteredTournaments.map((t) => (
                      <div key={t._id} className="animate-fade-in-up" style={{ animationDelay: `${(filteredTournaments.indexOf(t) % 6) * 0.1}s` }}>
                        <TournamentCard tournament={t} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-40 bg-white/[0.02] border border-white/5 rounded-[48px] relative overflow-hidden group">
              <div className="absolute inset-0 bg-primary-orange/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative z-10">
                <div className="w-20 h-20 bg-white/5 rounded-[32px] flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform duration-500">
                  <Search className="text-white/20" size={40} />
                </div>
                <h3 className="text-3xl font-black text-white mb-4">Ups! No hay nada por acá</h3>
                <p className="text-white/40 max-w-sm mx-auto font-medium text-lg mb-10">Intentá con otros términos o buscá por productora.</p>
                <button
                  onClick={() => setSlug("")}
                  className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full font-black text-sm uppercase tracking-widest transition-all"
                >
                  Limpiar Búsqueda
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      <PublicFooter />

      {/* SALON PICKER MODAL */}
      {showPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setShowPicker(false)}>
          <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-[40px] overflow-hidden animate-fade-in-up" onClick={e => e.stopPropagation()}>
            <div className="px-8 pt-8 pb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black text-white">Mis Espacios</h2>
                <p className="text-xs font-bold text-white/30 uppercase tracking-widest">Elegí a dónde querés ir</p>
              </div>
              <button onClick={() => setShowPicker(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-all">
                <X size={20} />
              </button>
            </div>
            <div className="px-8 pb-8 space-y-3">
              {(Array.isArray(mySalons) ? mySalons : []).map(s => (
                <button
                  key={s.id}
                  onClick={() => { navigate(`/${s.slug}`); setShowPicker(false); }}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between group hover:border-primary-orange/50 transition-all"
                >
                  <div className="text-left">
                    <p className="font-black text-white group-hover:text-primary-orange transition-colors">{s.nombre}</p>
                    <p className="text-[10px] font-bold text-white/20 uppercase tracking-widest">{s.role === 'admin' ? 'Administrador' : 'Cliente'}</p>
                  </div>
                  <ChevronRight size={20} className="text-white/20 group-hover:text-primary-orange group-hover:translate-x-1 transition-all" />
                </button>
              ))}
              <div className="pt-2 border-t border-white/10 mt-2 space-y-2">
                <button
                  onClick={() => { setShowPicker(false); setShowOnboardingModal(true); }}
                  className="w-full bg-transparent border border-dashed border-white/20 hover:border-primary-orange hover:text-primary-orange rounded-2xl py-3 px-4 flex items-center justify-center gap-2 group transition-all text-white/50 font-bold text-xs"
                >
                  <span>+</span> Crear Perfil Creador (Casual)
                </button>
                <button
                  onClick={() => { setShowPicker(false); navigate('/'); }}
                  className="w-full bg-transparent border border-dashed border-white/20 hover:border-blue-400 hover:text-blue-400 rounded-2xl py-3 px-4 flex items-center justify-center gap-2 group transition-all text-white/50 font-bold text-xs"
                >
                  <span>+</span> Registrar Salón o Productora
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE MENU MODAL */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-[#080808] flex flex-col p-6 animate-fade-in-up sm:hidden">
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-brand-gradient rounded-xl flex items-center justify-center">
                <Ticket className="text-white" size={20} />
              </div>
              <span className="font-black tracking-tighter text-2xl text-white">PASSAR</span>
            </div>
            <button onClick={() => setIsMobileMenuOpen(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-all">
              <X size={20} />
            </button>
          </div>
          <div className="flex flex-col gap-6 text-2xl font-black">
            <Link to="/explorar" onClick={() => setIsMobileMenuOpen(false)} className="text-white hover:text-primary-orange transition-colors">Explorar Socios</Link>
            
            <div className="flex flex-col gap-1 border-y border-white/10 py-5 my-2">
              <button onClick={() => { setIsMobileMenuOpen(false); handleCreateEventClick(); }} className="text-left text-white hover:text-primary-orange transition-colors">Crear Un Evento Único <span className="text-xs block text-white/40 font-medium mt-1">Para creadores independientes y eventos casuales</span></button>
            </div>
            
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-blue-400 hover:text-blue-300 transition-colors">Registrar Salón / Productora <span className="text-xs block text-blue-400/50 font-medium mt-1">Para espacios y empresas de eventos</span></Link>

            <a href="/suport" onClick={() => setIsMobileMenuOpen(false)} className="text-white hover:text-primary-orange transition-colors mt-2">Soporte</a>
            {isAuthenticated && (
              <button
                onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
                className="text-red-400 text-left hover:text-red-300 transition-colors"
              >
                Cerrar Sesión
              </button>
            )}
          </div>
        </div>
      )}

      {/* AUTO ONBOARDING MODAL */}
      {showOnboardingModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setShowOnboardingModal(false)}>
          <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 rounded-[40px] overflow-hidden animate-fade-in-up" onClick={e => e.stopPropagation()}>
            <div className="px-8 pt-8 pb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-black text-white">Tu Perfil de Creador</h2>
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest mt-1">Cómo se llamará la cuenta donde publicarás tus eventos?</p>
              </div>
              <button disabled={onboardingLoading} onClick={() => setShowOnboardingModal(false)} className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-all disabled:opacity-50">
                <X size={20} />
              </button>
            </div>
            <div className="px-8 pb-8">
              <form onSubmit={handleOnboardingSubmit} className="space-y-6">
                <div>
                  <input
                    autoFocus
                    type="text"
                    value={onboardingName}
                    onChange={(e) => setOnboardingName(e.target.value)}
                    placeholder="Ej: Eventos de Juan"
                    className="w-full bg-[#121212] border border-white/10 rounded-2xl py-4 px-5 text-sm text-white font-medium outline-none focus:border-primary-orange/50 transition-all shadow-sm"
                    required
                  />
                  <p className="text-[10px] text-white/30 mt-3 font-bold">Generaremos automáticamente un link único para que compartas tu perfil. Luego podrás conectar tu Mercado Pago y empezar a vender tus entradas de forma 100% gratuita para vos (los invitados abonan el cargo de servicio).</p>
                </div>
                <button
                  type="submit"
                  disabled={onboardingLoading || !onboardingName.trim()}
                  className="w-full py-4 bg-primary-orange text-[#0A0A0A] font-black uppercase tracking-widest text-sm rounded-2xl hover:bg-[#ff7b00] transition-colors shadow-[0_0_20px_rgba(255,107,0,0.3)] disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {onboardingLoading ? 'Creando tu espacio...' : 'Comenzar a Vender'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
